import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, LockKeyhole, ShieldAlert } from "lucide-react";
import { GuardianProvider } from "./contexts/GuardianContext";
import { usePortfolio } from "./contexts/PortfolioContext";
import {
  hasSupabaseConfig,
  supabase,
  supabaseConfigError,
} from "./lib/supabaseClient";
import HqsCommandCenter from "./nexus/HqsCommandCenter";
import OwnerAccessPage from "./pages/OwnerAccessPage";
import ResearchOAuthConsentPage from "./pages/ResearchOAuthConsentPage";

const OWNER_PROBE_PATH = "/api/admin/agent-status";

function SetupMissingCard() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#020711] p-6">
      <div className="max-w-xl rounded-2xl border border-slate-700 bg-hqs-surface p-8 shadow-lg glass-card">
        <h1 className="mb-4 text-2xl font-bold text-slate-100">Owner-Setup fehlt</h1>
        <p className="text-slate-300">
          Für den geschützten HQS NEXUS Zugang müssen
          <code className="mx-1 rounded bg-slate-800 px-2 py-1 font-mono text-cyan-300">VITE_SUPABASE_URL</code>
          und
          <code className="mx-1 rounded bg-slate-800 px-2 py-1 font-mono text-cyan-300">VITE_SUPABASE_ANON_KEY</code>
          gesetzt sein.
        </p>
        {supabaseConfigError && (
          <p className="mt-4 rounded-lg border border-red-700/50 bg-red-900/30 p-3 text-sm text-red-300">
            {supabaseConfigError}
          </p>
        )}
      </div>
    </div>
  );
}

function LoadingGate({ label = "Owner-Berechtigung wird geprüft..." }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#020711] p-6">
      <div className="flex items-center rounded-xl border border-cyan-300/10 bg-[#07111f]/90 px-5 py-4 shadow-xl">
        <Loader2 className="mr-3 h-5 w-5 animate-spin text-cyan-300" />
        <span className="text-sm text-slate-300">{label}</span>
      </div>
    </div>
  );
}

function AccessDeniedCard({ detail, onSignOut, onRetry, unavailable = false, signingOut, signOutError }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#020711] p-6 text-slate-100">
      <div className="w-full max-w-lg rounded-[24px] border border-rose-400/15 bg-[#07111f]/95 p-8 shadow-[0_30px_100px_rgba(0,0,0,.45)]">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-rose-400/20 bg-rose-500/8 text-rose-300">
          <ShieldAlert size={22} />
        </div>
        <p className="mt-6 text-[10px] font-bold uppercase tracking-[.16em] text-rose-300/80">
          {unavailable ? "Verbindung unterbrochen" : "Zugriff geschützt"}
        </p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight">{unavailable ? "Prüfung derzeit nicht möglich" : "Kein Owner-Zugriff"}</h1>
        <p className="mt-3 text-sm leading-7 text-slate-400">
          {unavailable ? "Die Verbindung konnte nicht bestätigt werden. Du kannst die Prüfung erneut starten." : "Dieser Bereich enthält interne HQS Daten und ist ausschließlich für den freigegebenen Owner-Account zugänglich."}
        </p>
        {detail && (
          <p className="mt-4 rounded-xl border border-white/5 bg-white/[.025] p-3 text-xs leading-5 text-slate-500">
            {detail}
          </p>
        )}
        <div className="mt-7 flex flex-wrap gap-3">
          {unavailable ? <button type="button" onClick={onRetry} className="inline-flex min-h-11 items-center rounded-xl bg-cyan-300 px-4 text-sm font-semibold text-slate-950">Erneut versuchen</button> : null}
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-slate-100 px-4 text-sm font-semibold text-slate-950"
          >
            <LockKeyhole size={15} />
            {signingOut ? "Wird abgemeldet …" : "Abmelden"}
          </button>
          <a
            href="/"
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-700 px-4 text-sm font-semibold text-slate-300"
          >
            <ArrowLeft size={15} />
            Öffentliche Website
          </a>
        </div>
        {signOutError ? <p role="alert" className="mt-4 text-sm text-rose-300">{signOutError}</p> : null}
      </div>
    </div>
  );
}

function IntegratedAppShell() {
  const { positions } = usePortfolio();
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [ownerState, setOwnerState] = useState({ status: "idle", detail: "" });
  const [loginError, setLoginError] = useState("");
  const [ownerAttempt, setOwnerAttempt] = useState(0);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState("");

  useEffect(() => {
    if (!hasSupabaseConfig || !supabase) return undefined;

    let mounted = true;
    let authRevision = 0;
    const timer = window.setTimeout(() => {
      if (!mounted) return;
      authRevision += 1;
      setLoginError("Die Sitzung konnte nicht rechtzeitig geladen werden. Bitte erneut anmelden.");
      setAuthLoading(false);
    }, 20_000);
    // Subscribe before reading the session so sign-in/out events cannot be
    // lost during bootstrap. Keep the callback synchronous.
    const listener = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;
      authRevision += 1;
      window.clearTimeout(timer);
      setSession(nextSession ?? null);
      setAuthLoading(false);
      if (!nextSession) setOwnerState({ status: "idle", detail: "" });
    });
    const subscription = listener?.data?.subscription;
    const revision = authRevision;

    const bootstrap = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!mounted || authRevision !== revision) return;
        if (error) throw error;
        setSession(data.session ?? null);
      } catch {
        if (!mounted || authRevision !== revision) return;
        setSession(null);
        setLoginError("Die Sitzung konnte nicht geladen werden. Bitte erneut anmelden.");
      } finally {
        window.clearTimeout(timer);
        if (mounted && authRevision === revision) setAuthLoading(false);
      }
    };

    void bootstrap();

    return () => {
      mounted = false;
      window.clearTimeout(timer);
      subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!session?.access_token) return undefined;

    let cancelled = false;
    const controller = new AbortController();
    let timedOut = false;
    const timer = window.setTimeout(() => { timedOut = true; controller.abort(); }, 20_000);

    const verifyOwner = async () => {
      setLoginError("");
      setOwnerState({ status: "checking", detail: "" });

      try {
        // Probe the exact same protected Railway admin surface used by NEXUS.
        // hqs-backend mounts ownerOrAdminAuth on /api/admin/* and validates the
        // Supabase bearer token against the single configured HQS owner UUID.
        // This avoids a second, divergent Vercel-side Supabase verifier and
        // guarantees that a successful gate means the real dashboard auth path
        // is usable as well.
        const response = await fetch(OWNER_PROBE_PATH, {
          method: "GET",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => null);
        if (cancelled) return;
        if (timedOut) throw new Error("Owner verification timed out");

        if (response.ok) {
          setOwnerState({ status: "approved", detail: "", token: session.access_token });
          return;
        }

        if (response.status === 401) {
          const message =
            payload?.error ||
            "Die Supabase-Anmeldung war erfolgreich, aber der Backend-Owner-Zugang konnte die Session nicht bestätigen.";
          setLoginError(message);
          setOwnerState({ status: "denied", detail: message });
          const { error: signOutFailure } = await supabase.auth.signOut();
          if (signOutFailure && !cancelled) setSignOutError("Abmelden war nicht möglich. Bitte erneut versuchen.");
          return;
        }

        if (response.status === 403) {
          setOwnerState({
            status: "denied",
            detail:
              payload?.error ||
              "Der angemeldete Account ist nicht als HQS Owner freigegeben.",
          });
          return;
        }

        setOwnerState({
          status: "error",
          detail:
            payload?.error ||
            `Owner-Prüfung am produktiven Admin-Pfad fehlgeschlagen (HTTP ${response.status}).`,
        });
      } catch {
        if (cancelled) return;
        setOwnerState({
          status: "error",
          detail:
            timedOut ? "Die Verbindung hat zu lange gedauert. Bitte die Prüfung erneut starten." : "Die Verbindung zum Owner-Bereich konnte nicht hergestellt werden. Bitte erneut versuchen.",
        });
      } finally {
        window.clearTimeout(timer);
      }
    };

    void verifyOwner();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [session?.access_token, ownerAttempt]);

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    setSignOutError("");
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setSession(null);
      setOwnerState({ status: "idle", detail: "" });
    } catch {
      setSignOutError("Abmelden war nicht möglich. Bitte erneut versuchen.");
    } finally {
      setSigningOut(false);
    }
  }

  if (!hasSupabaseConfig || !supabase) {
    return <SetupMissingCard />;
  }

  if (authLoading) {
    return <LoadingGate label="Sichere Session wird geladen..." />;
  }

  if (!session?.user) {
    return <OwnerAccessPage initialError={loginError} />;
  }

  if (ownerState.status === "idle" || ownerState.status === "checking" || (ownerState.status === "approved" && ownerState.token !== session.access_token)) {
    return <LoadingGate />;
  }

  if (ownerState.status === "denied" || ownerState.status === "error") {
    return (
      <AccessDeniedCard
        detail={ownerState.detail}
        onSignOut={() => void handleSignOut()}
        signingOut={signingOut}
        signOutError={signOutError}
        unavailable={ownerState.status === "error"}
        onRetry={() => { setOwnerState({ status: "checking", detail: "" }); setOwnerAttempt((attempt) => attempt + 1); }}
      />
    );
  }

  if (window.location.pathname.replace(/\/+$/, "") === "/oauth/consent") {
    return <ResearchOAuthConsentPage />;
  }

  return (
    <GuardianProvider portfolio={positions}>
      <HqsCommandCenter onSignOut={() => void handleSignOut()} signingOut={signingOut} signOutError={signOutError} />
    </GuardianProvider>
  );
}

export default function App() {
  return <IntegratedAppShell />;
}
